import React, { useEffect, useState, useRef } from 'react';
import { ProgressTile, SessionStats } from '@/types';
import { createProgressTileAnimation } from '@/utils/animationUtils';

interface ProgressVisualizationProps {
  progressTiles: ProgressTile[];
  maxTiles?: number;
  className?: string;
  sessionStats?: SessionStats;
  onNewRound?: () => void;
}

// 🔧 PHASE 1 FIX: Memory management for progress tiles
const MEMORY_SAFE_MAX_TILES = 100;
const ANIMATION_CLEANUP_DELAY = 5000; // 5 seconds

export const ProgressVisualization: React.FC<ProgressVisualizationProps> = ({
  progressTiles,
  maxTiles = MEMORY_SAFE_MAX_TILES, // 🔧 PHASE 1 FIX: Reduced default to prevent memory leaks
  className = '',
  sessionStats,
  onNewRound
}) => {
  const [visibleTiles, setVisibleTiles] = useState<ProgressTile[]>([]);
  const animationTimeoutRefs = useRef<Map<string, number>>(new Map());

  /**
   * 🔧 PHASE 1 FIX: Memory-safe tile management
   */
  useEffect(() => {
    // 🔧 CRITICAL: Enforce memory limits more strictly
    const safeTileLimit = Math.min(maxTiles, MEMORY_SAFE_MAX_TILES);
    const recentTiles = progressTiles.slice(-safeTileLimit);
    
    setVisibleTiles(recentTiles);
    
    // 🔧 MEMORY: Clean up old animation timeouts
    const currentTileIds = new Set(recentTiles.map(tile => tile.id));
    animationTimeoutRefs.current.forEach((timeoutId, tileId) => {
      if (!currentTileIds.has(tileId)) {
        clearTimeout(timeoutId);
        animationTimeoutRefs.current.delete(tileId);
      }
    });
    
    console.log(`📊 Visible tiles: ${recentTiles.length}, Timeouts: ${animationTimeoutRefs.current.size}`);
  }, [progressTiles, maxTiles]);

  /**
   * 🔧 PHASE 1 FIX: Memory-safe animation management
   */
  useEffect(() => {
    if (progressTiles.length > 0) {
      const latestTile = progressTiles[progressTiles.length - 1];
      const tileElement = document.getElementById(`tile-${latestTile.id}`);
      
      if (tileElement) {
        createProgressTileAnimation(tileElement, latestTile.type);
        
        // 🔧 MEMORY: Set cleanup timeout for animation
        const timeoutId = window.setTimeout(() => {
          animationTimeoutRefs.current.delete(latestTile.id);
        }, ANIMATION_CLEANUP_DELAY);
        
        animationTimeoutRefs.current.set(latestTile.id, timeoutId);
      }
    }
  }, [progressTiles]);
  
  /**
   * 🔧 PHASE 1 FIX: Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Clear all animation timeouts
      animationTimeoutRefs.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      animationTimeoutRefs.current.clear();
    };
  }, []);

  /**
   * Calculates grid dimensions based on available tiles
   */
  const getGridDimensions = () => {
    const tileCount = Math.min(visibleTiles.length, maxTiles);
    const cols = Math.ceil(Math.sqrt(tileCount * 1.5)); // Slightly wider than square
    const rows = Math.ceil(tileCount / cols);
    return { cols, rows };
  };

  const { cols, rows } = getGridDimensions();

  return (
    <div className={`progress-visualization ${className}`}>
      <div className="progress-header">
        <div className="progress-title">
          {sessionStats ? 'Training Complete!' : 'Your Typing Progress'}
        </div>
        {sessionStats && (
          <div className="completion-stats">
            <div className="stat-item">
              <span className="stat-label">Accuracy</span>
              <span className="stat-value">{sessionStats.accuracy.toFixed(1)}%</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Speed</span>
              <span className="stat-value">{sessionStats.speed.toFixed(1)} L/s</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">WPM</span>
              <span className="stat-value">{sessionStats.wpm.toFixed(0)}</span>
            </div>
          </div>
        )}
        <div className="progress-legend">
          <div className="legend-item">
            <div className="legend-dot correct"></div>
            <span>Correct</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot incorrect"></div>
            <span>Wrong</span>
          </div>
        </div>
      </div>

      <div 
        className="progress-grid"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`
        }}
      >
        {visibleTiles.map((tile, index) => (
          <div
            key={tile.id}
            id={`tile-${tile.id}`}
            className={`progress-tile ${tile.type}`}
            style={{
              animationDelay: `${index * 10}ms`
            }}
            title={`${tile.type === 'correct' ? 'Correct' : 'Incorrect'} keystroke at ${new Date(tile.timestamp).toLocaleTimeString()}`}
          />
        ))}
        
        {/* Empty placeholders for visual consistency */}
        {Array.from({ length: Math.max(0, maxTiles - visibleTiles.length) }).map((_, index) => (
          <div
            key={`empty-${index}`}
            className="progress-tile empty"
          />
        ))}
      </div>

      <div className="progress-summary">
        <div className="summary-item">
          <span className="summary-label">Total:</span>
          <span className="summary-value">{visibleTiles.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Correct:</span>
          <span className="summary-value correct">
            {visibleTiles.filter(t => t.type === 'correct').length}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Wrong:</span>
          <span className="summary-value incorrect">
            {visibleTiles.filter(t => t.type === 'incorrect').length}
          </span>
        </div>
      </div>

      {/* New Round Button for completed sessions */}
      {sessionStats && onNewRound && (
        <div className="new-round-section">
          <button 
            onClick={onNewRound}
            className="new-round-btn"
          >
            New Round
          </button>
        </div>
      )}

      <style>{`
        .progress-visualization {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 20px;
          background: rgba(17, 24, 39, 0.8);
          border-radius: 12px;
          border: 1px solid rgba(75, 85, 99, 0.3);
          backdrop-filter: blur(10px);
          min-width: 300px;
          max-width: 400px;
        }

        .progress-header {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .progress-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #ffffff;
          text-align: center;
          margin-bottom: 8px;
        }

        .completion-stats {
          display: flex;
          justify-content: space-between;
          margin: 12px 0;
          padding: 12px;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 8px;
        }

        .completion-stats .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .completion-stats .stat-label {
          font-size: 0.75rem;
          color: #9ca3af;
          text-transform: uppercase;
          font-weight: 500;
        }

        .completion-stats .stat-value {
          font-size: 1rem;
          font-weight: 700;
          color: #22c55e;
          font-family: 'Courier New', monospace;
        }

        .progress-legend {
          display: flex;
          justify-content: center;
          gap: 20px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .legend-dot.correct {
          background: #22c55e;
          box-shadow: 0 0 8px rgba(34, 197, 94, 0.4);
        }

        .legend-dot.incorrect {
          background: #ef4444;
          box-shadow: 0 0 8px rgba(239, 68, 68, 0.4);
        }

        .progress-grid {
          display: grid;
          gap: 2px;
          padding: 12px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          min-height: 120px;
          max-height: 200px;
          overflow: hidden;
        }

        .progress-tile {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          transition: all 0.2s ease-out;
          cursor: pointer;
        }

        .progress-tile.correct {
          background: #22c55e;
          box-shadow: 0 0 4px rgba(34, 197, 94, 0.3);
        }

        .progress-tile.incorrect {
          background: #ef4444;
          box-shadow: 0 0 4px rgba(239, 68, 68, 0.3);
        }

        .progress-tile.empty {
          background: rgba(75, 85, 99, 0.3);
          border: 1px solid rgba(75, 85, 99, 0.2);
        }

        .progress-tile:hover:not(.empty) {
          transform: scale(1.5);
          z-index: 10;
        }

        .progress-summary {
          display: flex;
          justify-content: space-around;
          padding: 12px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .summary-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          min-width: 70px;
          flex: 1;
        }

        .summary-label {
          font-size: 0.625rem;
          color: #9ca3af;
          text-transform: uppercase;
          font-weight: 500;
          letter-spacing: 0.05em;
        }

        .summary-value {
          font-size: 0.875rem;
          font-weight: 700;
          color: #ffffff;
          font-family: 'Courier New', monospace;
        }

        .summary-value.correct {
          color: #22c55e;
        }

        .summary-value.incorrect {
          color: #ef4444;
        }

        /* Animation for new tiles */
        @keyframes tileAppear {
          from {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          to {
            transform: scale(1) rotate(360deg);
            opacity: 1;
          }
        }

        .progress-tile.correct {
          animation: tileAppear 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .progress-tile.incorrect {
          animation: tileAppear 0.4s ease-out;
        }

        /* Pulse effect for active typing */
        .progress-visualization.active .progress-grid {
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .progress-visualization {
            min-width: 250px;
            max-width: 300px;
            padding: 16px;
          }

          .progress-grid {
            min-height: 100px;
            max-height: 150px;
            padding: 8px;
          }

          .progress-tile {
            width: 6px;
            height: 6px;
          }

          .progress-summary {
            padding: 8px;
          }

          .summary-item {
            min-width: 50px;
          }
        }

        /* Achievement effects */
        .progress-visualization.milestone {
          animation: milestoneGlow 1s ease-in-out;
        }

        @keyframes milestoneGlow {
          0%, 100% { 
            box-shadow: 0 0 0 rgba(34, 197, 94, 0); 
          }
          50% { 
            box-shadow: 0 0 30px rgba(34, 197, 94, 0.3),
                        inset 0 0 30px rgba(34, 197, 94, 0.1); 
          }
        }

        /* Pattern recognition for streaks */
        .progress-tile.streak {
          background: linear-gradient(45deg, #22c55e, #16a34a);
          animation: streakPulse 0.5s ease-in-out;
        }

        @keyframes streakPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        .new-round-section {
          margin-top: 16px;
          display: flex;
          justify-content: center;
        }

        .new-round-btn {
          padding: 12px 24px;
          background: linear-gradient(45deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease-out;
        }

        .new-round-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
        }
      `}</style>
    </div>
  );
};

export default ProgressVisualization;
